import { Controller } from "@hotwired/stimulus"
import { Spring, prefersReducedMotion } from "../lib/spring"
import { landed } from "../lib/feedback"

// Waits for the certificate's PDF. Polls with fetch (which never interrupts
// the user's own requests), quickly at first since most jobs finish within a
// few seconds, then every few seconds, and gives up after a time budget
// with a manual retry.
//
// Once the server stops rendering the pending panel (marked data-poll-pending)
// the work is done. With `swap` naming page regions by id, only those regions
// are replaced from the fresh page — so an email being written meanwhile
// survives — after the status list has marked the step done. Without it the
// page is visited again once, as before.
export default class extends Controller {
  static targets = ["status", "error", "line", "tick"]
  static values = {
    interval: { type: Number, default: 3000 },
    maxAttempts: { type: Number, default: 20 },
    swap: { type: String, default: "" }
  }

  static EARLY = [600, 1000, 1500, 2000] // ms between the first checks

  connect() {
    this.checks = 0
    this.deadline = Date.now() + this.intervalValue * this.maxAttemptsValue
    this.schedule()
  }

  disconnect() {
    this.stop()
  }

  schedule() {
    const early = this.constructor.EARLY
    const wait = this.checks < early.length ? early[this.checks] : this.intervalValue
    this.timer = setTimeout(() => this.check(), wait)
  }

  stop() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  async check() {
    this.checks++
    if (Date.now() > this.deadline) return this.fail()

    try {
      const response = await fetch(window.location.href, {
        headers: { Accept: "text/html" },
        cache: "no-store"
      })
      if (!response.ok) return this.schedule()
      const html = await response.text()
      if (html.includes("data-poll-pending")) return this.schedule()
      this.stop()
      if (this.swapValue) await this.swap(html)
      else this.reload()
    } catch (_) {
      // transient network error — try again on the next tick
      this.schedule()
    }
  }

  // The regions named in `swap`, replaced from the fresh document once the
  // status list has said "ready". If the page has changed shape meanwhile
  // (it was sent from elsewhere), a full visit is the honest fallback.
  async swap(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const regions = this.swapValue.split(" ").filter(Boolean).map(id => [ document.getElementById(id), doc.getElementById(id) ])
    if (regions.some(([ current, fresh ]) => !current || !fresh)) return this.reload()

    await this.ready()
    regions.forEach(([ current, fresh ]) => current.replaceWith(fresh))
  }

  // Completion: the rendering line becomes done, the tick lands with weight
  // and a haptic on the same frame, the live region says so.
  ready() {
    const rendering = this.lineTargets.find(line => line.dataset.state === "current")
    const next = this.lineTargets.find(line => line.dataset.state === "todo")
    if (!rendering) return Promise.resolve()

    rendering.dataset.state = "done"
    if (next) next.dataset.state = "current"
    if (this.hasStatusTarget) this.statusTarget.setAttribute("aria-label", "Your certificate is ready")

    const tick = rendering.querySelector("[data-poll-target='tick']")
    landed()
    if (!tick || prefersReducedMotion()) return Promise.resolve()

    return new Promise(resolve => {
      const spring = new Spring({
        value: 0.6,
        response: 0.3,
        damping: 1,
        onUpdate: (value) => { tick.style.transform = `scale(${value.toFixed(3)})`; tick.style.opacity = String(Math.min(1, value)) },
        onRest: () => { tick.style.removeProperty("transform"); tick.style.removeProperty("opacity"); resolve() }
      })
      spring.to(1)
      // Whatever happens to the frame clock, the page does not wait long.
      setTimeout(resolve, 600)
    })
  }

  reload() {
    this.stop()
    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  fail() {
    this.stop()
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden")
      this.errorTarget.classList.add("flex")
    }
  }
}
